import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FlashOn as FlashOnIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Memory as MemoryIcon,
  Queue as QueueIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Timer as TimerIcon,
  ExpandMore as ExpandMoreIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface MemoryStats {
  system: string;
  stats: {
    totalLeads: number;
    activeFollowups: number;
    queuedFollowups: number;
    followupsSent: number;
    responsesReceived: number;
    errors: number;
    activeLeads: number;
    activeTimers: number;
    memory_usage: {
      leadStates: number;
      templates: number;
      campaigns: number;
      executionQueue: number;
    };
  };
  message: string;
}

interface QueuedFollowup {
  lead_id: string;
  campaign_id: string;
  step_id: string;
  scheduled_at: string;
  template_id: string;
  retry_count: number;
}

interface LeadState {
  contact_id: string;
  conversation_id: string;
  channel_type: string;
  last_activity_at: string;
  current_step: number;
  followups_sent: number;
  has_responded: boolean;
  is_paused: boolean;
  campaign_id?: string;
  next_followup_at?: string;
}

const MemoryFollowupMonitor: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [queuedFollowups, setQueuedFollowups] = useState<QueuedFollowup[]>([]);
  const [leadStates, setLeadStates] = useState<LeadState[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadState | null>(null);
  const [leadDetailsOpen, setLeadDetailsOpen] = useState(false);

  // Carrega dados do sistema de memória
  const loadData = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get('/api/v1/followups/memory/status');
      
      if (response.data.success) {
        setStats(response.data.data);
        setLastUpdate(new Date());
      } else {
        setError('Erro ao carregar status do sistema');
      }
    } catch (error) {
      console.error('Error loading memory followup data:', error);
      setError('Erro ao carregar dados do sistema de memória');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega detalhes da fila de execução
  const loadQueueDetails = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/followups/memory/queue-details');
      if (response.data.success) {
        setQueuedFollowups(response.data.data.queuedFollowups || []);
        setLeadStates(response.data.data.leadStates || []);
      }
    } catch (error) {
      console.error('Error loading queue details:', error);
    }
  }, []);

  // Recarrega sistema de memória
  const reloadSystem = async () => {
    try {
      setProcessing(true);
      setError(null);
      const response = await axios.post('/api/v1/followups/memory/reload');
      
      if (response.data.success) {
        await loadData();
        await loadQueueDetails();
        alert('Sistema de memória recarregado com sucesso!');
      } else {
        setError('Erro ao recarregar sistema');
      }
    } catch (error) {
      console.error('Error reloading system:', error);
      setError('Erro ao recarregar sistema de memória');
    } finally {
      setProcessing(false);
    }
  };

  // Pausa/Retoma sistema
  const toggleSystem = async (pause: boolean) => {
    try {
      setProcessing(true);
      setError(null);
      const response = await axios.post('/api/v1/followups/memory/pause', { pause });
      
      if (response.data.success) {
        await loadData();
        alert(pause ? 'Sistema pausado' : 'Sistema retomado');
      } else {
        setError('Erro ao alterar estado do sistema');
      }
    } catch (error) {
      console.error('Error toggling system:', error);
      setError('Erro ao alterar estado do sistema');
    } finally {
      setProcessing(false);
    }
  };

  // Atualiza atividade de um lead
  const updateLeadActivity = async (contactId: string) => {
    try {
      setProcessing(true);
      setError(null);
      const response = await axios.post('/api/v1/followups/memory/update-activity', {
        contact_id: contactId
      });
      
      if (response.data.success) {
        await loadData();
        await loadQueueDetails();
        alert('Atividade do lead atualizada!');
      } else {
        setError('Erro ao atualizar atividade do lead');
      }
    } catch (error) {
      console.error('Error updating lead activity:', error);
      setError('Erro ao atualizar atividade do lead');
    } finally {
      setProcessing(false);
    }
  };

  // Efeitos
  useEffect(() => {
    loadData();
    loadQueueDetails();
  }, [loadData, loadQueueDetails]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
      if (showDetails) {
        loadQueueDetails();
      }
    }, 10000); // Atualiza a cada 10 segundos

    return () => clearInterval(interval);
  }, [autoRefresh, loadData, loadQueueDetails, showDetails]);

  const getStatusColor = () => {
    if (!stats) return 'default';
    if (stats.stats.errors > 0) return 'error';
    if (stats.stats.queuedFollowups > 0) return 'success';
    return 'warning';
  };

  const getStatusIcon = () => {
    if (!stats) return <WarningIcon />;
    if (stats.stats.errors > 0) return <ErrorIcon />;
    if (stats.stats.queuedFollowups > 0) return <CheckCircleIcon />;
    return <WarningIcon />;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getTimeUntilExecution = (scheduledAt: string) => {
    const now = new Date();
    const scheduled = new Date(scheduledAt);
    const diff = scheduled.getTime() - now.getTime();
    
    if (diff <= 0) return 'Pronto para execução';
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box className="glass-blue-card rounded-xl">
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MemoryIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
              <Typography variant="h6" component="h2" sx={{ color: '#000000', fontWeight: 600 }}>
                Sistema de Followup - Memória
              </Typography>
              <Chip
                icon={getStatusIcon()}
                label={stats ? `${stats.stats.queuedFollowups} na fila` : 'Carregando...'}
                color={getStatusColor()}
                size="small"
                className="ml-2"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3b82f6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3b82f6',
                      },
                    }}
                  />
                }
                label={<Typography sx={{ color: '#475569', fontSize: '0.875rem' }}>Auto-refresh</Typography>}
                labelPlacement="start"
              />
              
              <Tooltip title="Recarregar Sistema">
                <IconButton
                  onClick={reloadSystem}
                  disabled={processing}
                  sx={{
                    color: '#3b82f6',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Mostrar Detalhes">
                <IconButton
                  onClick={() => setShowDetails(!showDetails)}
                  sx={{
                    color: showDetails ? '#3b82f6' : '#64748b',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    },
                  }}
                >
                  <AnalyticsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {stats && (
            <>
              {/* Estatísticas Principais */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Leads Totais */}
                <Grid item xs={12} sm={6} xl={3}>
                  <Card className="glass-blue-card">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(59, 130, 246, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PeopleIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                        Leads Totais
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                        {stats.stats.totalLeads.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Na Fila */}
                <Grid item xs={12} sm={6} xl={3}>
                  <Card className="glass-blue-card">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(59, 130, 246, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <QueueIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                        Na Fila
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                        {stats.stats.queuedFollowups.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Enviados */}
                <Grid item xs={12} sm={6} xl={3}>
                  <Card className="glass-blue-card">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(59, 130, 246, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <SendIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                        Enviados
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                        {stats.stats.followupsSent.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Respostas */}
                <Grid item xs={12} sm={6} xl={3}>
                  <Card className="glass-blue-card">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: 'rgba(59, 130, 246, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CheckCircleIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                        Respostas
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                        {stats.stats.responsesReceived.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Uso de Memória */}
              <Box className="glass-blue-card rounded-xl mb-3">
                <Accordion 
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        margin: '16px 0',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StorageIcon sx={{ color: '#3b82f6' }} />
                      <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                        Uso de Memória
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          Estados de Leads
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          {stats.stats.memory_usage.leadStates}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          Templates
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          {stats.stats.memory_usage.templates}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          Campanhas
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          {stats.stats.memory_usage.campaigns}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                          Fila de Execução
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          {stats.stats.memory_usage.executionQueue}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Detalhes da Fila */}
              {showDetails && (
                <Box className="glass-blue-card rounded-xl mb-3">
                  <Accordion 
                    defaultExpanded
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QueueIcon sx={{ color: '#3b82f6' }} />
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          Fila de Execução ({queuedFollowups.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {queuedFollowups.length === 0 ? (
                        <Typography sx={{ color: '#64748b' }}>
                          Nenhum followup na fila
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Lead ID</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Campanha</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Template</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Agendado para</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Tempo Restante</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Tentativas</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {queuedFollowups.map((followup, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#000000' }}>
                                      {followup.lead_id.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#000000' }}>
                                      {followup.campaign_id.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#000000' }}>
                                      {followup.template_id.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: '#000000' }}>
                                      {formatTime(followup.scheduled_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={getTimeUntilExecution(followup.scheduled_at)}
                                      color={getTimeUntilExecution(followup.scheduled_at).includes('Pronto') ? 'success' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={followup.retry_count}
                                      color={followup.retry_count > 0 ? 'warning' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              {/* Estados dos Leads */}
              {showDetails && (
                <Box className="glass-blue-card rounded-xl mb-3">
                  <Accordion 
                    sx={{
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ color: '#3b82f6' }} />
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          Estados dos Leads ({leadStates.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {leadStates.length === 0 ? (
                        <Typography sx={{ color: '#64748b' }}>
                          Nenhum lead ativo
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Lead ID</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Canal</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Última Atividade</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Step Atual</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Followups Enviados</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ color: '#000000', fontWeight: 600 }}>Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {leadStates.slice(0, 10).map((lead, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#000000' }}>
                                      {lead.contact_id.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={lead.channel_type}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        color: '#3b82f6',
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: '#000000' }}>
                                      {formatTime(lead.last_activity_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={lead.current_step}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        color: '#3b82f6',
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={lead.followups_sent}
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                        color: '#3b82f6',
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={lead.has_responded ? 'Respondeu' : lead.is_paused ? 'Pausado' : 'Ativo'}
                                      color={lead.has_responded ? 'success' : lead.is_paused ? 'warning' : 'default'}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Atualizar Atividade">
                                      <IconButton
                                        size="small"
                                        onClick={() => updateLeadActivity(lead.contact_id)}
                                        sx={{
                                          color: '#3b82f6',
                                          '&:hover': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                          },
                                        }}
                                      >
                                        <RefreshIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              {/* Informações do Sistema */}
              <Box className="glass-blue-card rounded-xl p-3 mt-3">
                <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                  Última atualização: {lastUpdate.toLocaleString('pt-BR')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                  Sistema: {stats.system}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {stats.message}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MemoryFollowupMonitor;

